'use client';

import type { ReactElement } from 'react';
import React from 'react';
import ReactDOM from 'react-dom';
import { ClientGate } from 'components/ClientGate';
import {
  getClassnames,
  getCustomStyles,
  useAccentNeutralBrand,
  useAccentNeutralColors,
  useBrandName,
  useCustomBrand,
  useDensity,
  useOutermostThemeProviderValues,
  useSizing,
  useThemeName,
} from 'theme-utils';
import type { Brands } from '@frameio/vapor-theme';
import { themeClassNames } from '@frameio/vapor-theme';
import { usePortalContext } from './PortalContext';

export type PortalProps = {
  /** Disables rendering children into a new container. */
  disabled?: boolean;
  children: ReactElement<any>;
  /**
   * Enable Popover content to inherit the theme from the outermost ThemeProvider instead of its closest parent ThemeProvider.
   * Defaults to `false`
   */
  hasOutermostTheme?: boolean;
};

export const portalRootIdentifer = '__portal';

const getBrandClassName = (brandName?: Brands) => {
  return brandName
    ? themeClassNames[brandName] ?? brandName
    : themeClassNames.fio;
};

/**
 * Portal
 *
 * Render a React tree in a different DOM hierarchy to prevent
 * parent styles from clipping or hiding content, such as
 * with Popovers, Tooltips, etc.
 */
function PortalComponent({
  disabled,
  children,
  hasOutermostTheme = false,
}: PortalProps) {
  const contextTarget = usePortalContext();
  const contextThemeName = useThemeName();
  const contextBrandName = useBrandName();
  const customBrand = useCustomBrand();
  const accentNeutralColors = useAccentNeutralColors();
  const accentNeutralBrand = useAccentNeutralBrand();
  const outermostThemeProviderValues = useOutermostThemeProviderValues();
  const density = useDensity();
  const sizing = useSizing();
  const target = contextTarget || document.body;

  if (disabled) {
    return children;
  }

  let themeName = contextThemeName;
  let brandName = contextBrandName;

  if (hasOutermostTheme && outermostThemeProviderValues) {
    themeName = outermostThemeProviderValues.themeName;
    brandName = outermostThemeProviderValues.brandName;
  }

  const customStyles = hasOutermostTheme
    ? {}
    : getCustomStyles({
        customBrand,
        accentNeutralBrand,
        accentNeutralColors,
      });

  return ReactDOM.createPortal(
    <div
      className={getClassnames(
        themeName ? themeClassNames[themeName] : undefined,
        getBrandClassName(brandName),
        density ? themeClassNames[`density-${density}`] : undefined,
        sizing ? themeClassNames[`sizing-${sizing}`] : undefined
      )}
      data-__portal={true}
      data-theme={themeName}
      data-theme-brand={brandName}
      data-theme-custom-background={
        accentNeutralColors?.backgroundColor || false
      }
      data-theme-custom-brand={customBrand?.primary || false}
      data-theme-custom-text={accentNeutralColors?.backgroundColor || false}
      style={{
        display: 'contents',
        ...customStyles,
      }}
    >
      {children}
    </div>,
    target
  );
}

export function Portal(props: PortalProps) {
  return (
    <ClientGate>
      <PortalComponent {...props} />
    </ClientGate>
  );
}
